/* @ts-ignore */
import queryStringHandler from "query-string-handler";
import { scrollToElement } from "@/components/global/scroll-to-element";

interface ResultsItem {
  title: string;
  url: string;
  description: string;
  isResourceDetail: boolean;
  resourceDetailCard?: ResultCardOptions;
}
interface ResultCardOptions {
  documentUrl?: string;
  link?: ResultCardLink;
  videoId?: string;
}
interface ResultCardLink {
  href?: string;
  target?: string;
  text?: string;
  title?: string;
}

const initSearchModals = () => {
  const searchPage = document.querySelector<HTMLDivElement>(".js-search-page");

  if (searchPage) {
    const searchPageModals = searchPage.querySelectorAll(".js-global-modal-block");

    if (searchPageModals && searchPageModals.length > 0) {
      searchPageModals.forEach((item) => {
        const openBtn = item.querySelectorAll<HTMLButtonElement>(".js-open-modal-btn");
        const closeBtn = item.querySelector<HTMLButtonElement>(".js-close-modal-btn");
        const trapFocusBtn = item.querySelector<HTMLButtonElement>(".js-trap-focus-btn");
        const wrapper = item.querySelector<HTMLDivElement>(".js-modal-wrapper");
        const modal = item.querySelector<HTMLDivElement>(".js-modal");

        if (openBtn && closeBtn && trapFocusBtn && wrapper && modal) {
          const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;

            if (target.classList.contains("js-modal-wrapper")) {
              closeModal();
            }
          };

          const handleEscape = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
              closeModal();
            }
          };

          const handleTabIndex = (isFocus: boolean) => {
            const focusableSelectors = `
                  a[href],
                  area[href],
                  input:not([disabled]),
                  select:not([disabled]),
                  textarea:not([disabled]),
                  button:not([disabled]),
                  iframe,
                  object,
                  embed
              `;

            const focusItems = modal.querySelectorAll<HTMLElement>(focusableSelectors);

            focusItems.forEach((item) => {
              if (isFocus) {
                item.tabIndex = 0;
              } else {
                item.tabIndex = -1;
              }
            });
          };

          const handleResetIframe = () => {
            const iframe = item.querySelector<HTMLIFrameElement>("iframe");
            // resets and stops iframe from playing on close
            if (iframe) {
              iframe.src = "";
            }
          };

          const handleIframeSrc = (id: string, title: string) => {
            const iframe = item.querySelector<HTMLIFrameElement>("iframe");
            // resets and stops iframe from playing on close
            if (iframe) {
              iframe.src = `//play.vidyard.com/${id}.html?`;
              if (title) {
                iframe.title = title;
              }
            }
          };

          const openModal = () => {
            wrapper.setAttribute("aria-hidden", "false");
            wrapper.setAttribute("data-isOpen", "true");
            handleTabIndex(true);
            document.addEventListener("keydown", handleEscape);
          };

          const closeModal = () => {
            wrapper.setAttribute("aria-hidden", "true");
            wrapper.setAttribute("data-isOpen", "false");
            handleTabIndex(false);
            handleResetIframe();
            document.removeEventListener("keydown", handleEscape);
          };

          const handleResetFocus = () => {
            // taps focus and resets to first item
            closeBtn.focus();
          };

          // handle cms content and update tab index on load to -1
          handleTabIndex(false);

          document.addEventListener("click", handleClickOutside);

          openBtn.forEach((btn) => {
            btn.addEventListener("click", () => {
              const id = btn.getAttribute("data-videoId");
              const title = btn.getAttribute("data-videoTitle") || "";

              if (id) {
                handleIframeSrc(id, title);
              }
              openModal();
            });
          });
          closeBtn.addEventListener("click", () => {
            closeModal();
          });
          trapFocusBtn.addEventListener("focus", handleResetFocus);
        }
      });
    }
  }
};

export default function SearchPage() {
  const wrapper: HTMLDivElement | null = document.querySelector(".js-search-page");
  const submitBtn: HTMLButtonElement | null | undefined = wrapper?.querySelector(".js-submit-btn");
  const clearBtn: HTMLButtonElement | null | undefined = wrapper?.querySelector(".js-clear-btn");
  const searchInput: HTMLInputElement | null | undefined = wrapper?.querySelector(".js-search-input");
  const formattedTitle: HTMLSpanElement | null | undefined = wrapper?.querySelector(".js-formatted-title");
  const resultsList: HTMLDivElement | null | undefined = wrapper?.querySelector(".js-search-results");
  const paginationWrapper: HTMLDivElement | null | undefined = wrapper?.querySelector(".js-pagination-wrapper");
  const prevPageBtn: HTMLButtonElement | null | undefined = wrapper?.querySelector<HTMLButtonElement>(".js-prev-btn");
  const nextPageBtn: HTMLButtonElement | null | undefined = wrapper?.querySelector<HTMLButtonElement>(".js-next-btn");
  const paginationList: HTMLUListElement | null | undefined = wrapper?.querySelector(".js-pagination-list");
  let totalPagesResult: number = 0;
  let results: ResultsItem[] = [];

  if (wrapper && formattedTitle && submitBtn && clearBtn && searchInput && paginationWrapper && paginationList && resultsList) {
    const handleCreatePaginationBtn = (index: number) => {
      const label = paginationWrapper.getAttribute("data-aria-label");
      const currentPage = parseInt(queryStringHandler.getValue("pg")) || 1;
      return `
            <li>
              <button aria-label="${label} ${index}" data-active="${index === currentPage ? "true" : "false"}" class="search-pagination-btn">${index}</button>
            </li>
        `;
    };

    const handleCreatePaginationElippsis = () => {
      return `
          <li class="search-pagination-ellipsis">
              <span>...</span>
          </li>
        `;
    };

    const handleResetActive = (activePage: string) => {
      const paginationItems = paginationList.querySelectorAll("li");

      paginationItems.forEach((item) => {
        const page: string | null = item.textContent;
        if (page) {
          item.setAttribute("data-active", page === activePage ? "true" : "false");
        }
      });
    };

    const handleRemovePaginationItems = () => {
      const paginationItems = paginationList.querySelectorAll("li");

      paginationItems.forEach((item) => item.remove());
    };

    const handleGoToPage = async (page: string) => {
      handlePageParam(page);
      handleResetActive(page);
      handleDisabledPagination();
      await handleFetchResults(searchInput.value, page);
      scrollToElement(wrapper);
    };

    const handlePaginationEventListeners = () => {
      const paginationBtns = paginationList.querySelectorAll<HTMLButtonElement>("button");

      paginationBtns.forEach((btn) => {
        const page: string | null = btn.textContent;
        if (page) {
          btn.addEventListener("click", () => handleGoToPage(page));
        }
      });
    };

    const handleCreatePagination = (totalPages: number) => {
      const currentPage = parseInt(queryStringHandler.getValue("pg")) || 1;
      handleRemovePaginationItems();
      // LAYOUT
      // X X X X X X
      if (totalPages < 7) {
        for (let i = 1; i < totalPages + 1; i++) {
          paginationList.insertAdjacentHTML("beforeend", handleCreatePaginationBtn(i));
        }
      }
      // LAYOUT
      // X X X X X ... X
      if (totalPages > 6 && currentPage < 4) {
        for (let i = 1; i < 7; i++) {
          if (i < 6) {
            paginationList.insertAdjacentHTML("beforeend", handleCreatePaginationBtn(i));
          }
          if (i === 6) {
            paginationList.insertAdjacentHTML("beforeend", handleCreatePaginationElippsis());
            paginationList.insertAdjacentHTML("beforeend", handleCreatePaginationBtn(totalPages));
          }
        }
      }
      // LAYOUT
      // X ... X X X ... X
      if (totalPages > 6 && currentPage > 3 && currentPage + 2 < totalPages) {
        const startPage = currentPage - 1;
        const endPage = currentPage + 1;

        paginationList.insertAdjacentHTML("beforeend", handleCreatePaginationBtn(1));
        paginationList.insertAdjacentHTML("beforeend", handleCreatePaginationElippsis());

        for (let i = startPage; i <= endPage; i++) {
          paginationList.insertAdjacentHTML("beforeend", handleCreatePaginationBtn(i));
        }

        paginationList.insertAdjacentHTML("beforeend", handleCreatePaginationElippsis());
        paginationList.insertAdjacentHTML("beforeend", handleCreatePaginationBtn(totalPages));
      }
      // LAYOUT
      // X ... X X X X X
      if (totalPages > 6 && currentPage > 3 && currentPage > totalPages - 3) {
        const startPage = totalPages - 4;

        paginationList.insertAdjacentHTML("beforeend", handleCreatePaginationBtn(1));
        paginationList.insertAdjacentHTML("beforeend", handleCreatePaginationElippsis());

        for (let i = startPage; i <= totalPages; i++) {
          paginationList.insertAdjacentHTML("beforeend", handleCreatePaginationBtn(i));
        }
      }

      handlePaginationEventListeners();
    };

    const handlePreviousPage = async () => {
      const currentPage = parseInt(queryStringHandler.getValue("pg")) || 1;
      const newPage = currentPage - 1;
      handlePageParam(newPage.toString());
      handleDisabledPagination();
      await handleFetchResults(searchInput.value, newPage.toString());
      scrollToElement(wrapper);
    };

    const handleNextPage = async () => {
      const currentPage = parseInt(queryStringHandler.getValue("pg")) || 1;
      const newPage = currentPage + 1;
      handlePageParam(newPage.toString());
      handleDisabledPagination();
      await handleFetchResults(searchInput.value, newPage.toString());
      scrollToElement(wrapper);
    };

    const handleDisabledPagination = () => {
      const currentPage = parseInt(queryStringHandler.getValue("pg")) || 1;
      if (totalPagesResult === 1) {
        paginationWrapper?.setAttribute("data-hide", "true");
      } else {
        paginationWrapper?.setAttribute("data-hide", "false");

        if (prevPageBtn) {
          if (currentPage === 1) {
            prevPageBtn.disabled = true;
          } else {
            prevPageBtn.disabled = false;
          }
        }

        if (nextPageBtn) {
          if (currentPage === totalPagesResult) {
            nextPageBtn.disabled = true;
          } else {
            nextPageBtn.disabled = false;
          }
        }
      }
    };

    if (prevPageBtn) {
      prevPageBtn.addEventListener("click", handlePreviousPage);
    }

    if (nextPageBtn) {
      nextPageBtn.addEventListener("click", handleNextPage);
    }

    const handleCreateSearchCard = (item: ResultsItem) => {
      return `        
          <div class="search-link-wrapper">
              <h3 class="search-link-title">${item.title}</h3>
              ${
                item.description
                  ? `
                <div class="search-link-desc">
                    ${item.description}
                </div>
              `
                  : ""
              }
          </div>
        `;
    };

    const handleCreatePageItem = (item: ResultsItem) => {
      return `
            <li class="search-result-item">                
                <a href="${item.url}" class="search-link">
                  ${handleCreateSearchCard(item)}
                </a>
            </li>
        `;
    };

    const handleCreateVideoCard = (item: ResultsItem) => {
      return `
        <li class="search-result-item">
            <div class="js-global-modal-block">
                <button data-videoId="${item.resourceDetailCard?.videoId}" data-videoTitle="${item.title}" class="js-open-modal-btn search-link">
                    ${handleCreateSearchCard(item)}
                </button>

                <div data-isOpen="false" 
                    class="
                        js-modal-wrapper
                        group/modal
                        fixed inset-0 bg-primary-800/60 z-[9999]
                        flex items-center justify-center
                        data-[isOpen=true]:opacity-100 
                        data-[isOpen=false]:opacity-0 data-[isOpen=false]:pointer-events-none
                        global-transition" 
                    role="dialog" aria-hidden="true" aria-modal="true" aria-labelledby="Video Player">

                    <div class="js-modal container mx-auto" tabindex="-1">
                        <div class="relative">
                            <button tabindex="-1" class="js-close-modal-btn focus:shadow-focus absolute top-4 right-4 z-10 h-10 w-10 rounded-full border border-primary-500 bg-primary-600 p-2 hover:no-underline flex items-center justify-center">
                              <span class="h-10 w-10 flex items-center justify-center">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
                                      <rect y="2.60938" width="3.69018" height="14.6938" transform="rotate(-45 0 2.60938)" fill="#FFF"/>
                                      <rect x="10.3906" width="3.69018" height="14.6938" transform="rotate(45 10.3906 0)" fill="#FFF"/>
                                  </svg>
                              </span>
                              <span class="sr-only">Close Modal</span>
                          </button>
                            <div class="max-h-[calc(100vh-100px)] overflow-auto relative flex justify-center group-data-[isOpen=true]/modal:scale-100 group-data-[isOpen=false]/modal:scale-90 global-transition">                                                  
                                <div class="flex w-full relative aspect-video">
                                    <iframe class="vidyard_iframe h-full w-full" width="640" height="360" scrolling="no" frameborder="0" allowtransparency="true" allowfullscreen referrerpolicy="no-referrer-when-downgrade"></iframe>
                                </div>
                            </div>
                            <button tabindex="-1" class="js-trap-focus-btn sr-only">
                                Refocus first element
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </li>
      `;
    };

    const handleCreateLinkCard = (item: ResultsItem) => {
      return `
        <li class="search-result-item">
          <a href="${item.resourceDetailCard?.link?.href}" target="${item.resourceDetailCard?.link?.target}" aria-label="${item.resourceDetailCard?.link?.title}" class="search-link">
            ${handleCreateSearchCard(item)}
          </a>
        </li>
      `;
    };

    const handleCreateDocumentCard = (item: ResultsItem) => {
      return `
        <li class="search-result-item">
          <a href="${item.resourceDetailCard?.documentUrl}" target="_blank" download class="search-link">
              ${handleCreateSearchCard(item)}
          </a>
        </li>
      `;
    };

    const handleResultItems = () => {
      if (results.length > 0) {
        wrapper.setAttribute("data-noresults", "false");
        resultsList.setAttribute("data-results", "true");

        results.forEach((item: ResultsItem) => {
          if (item.isResourceDetail) {
            if (item.resourceDetailCard?.documentUrl) {
              resultsList?.insertAdjacentHTML("beforeend", handleCreateDocumentCard(item));
            } else if (item.resourceDetailCard?.link?.href) {
              resultsList?.insertAdjacentHTML("beforeend", handleCreateLinkCard(item));
            } else if (item.resourceDetailCard?.videoId) {
              resultsList?.insertAdjacentHTML("beforeend", handleCreateVideoCard(item));
            }
          } else {
            resultsList?.insertAdjacentHTML("beforeend", handleCreatePageItem(item));
          }
        });
      } else {
        handleRemoveResults();
        wrapper.setAttribute("data-noresults", "true");
      }
    };

    const handleRemoveResults = () => {
      resultsList.setAttribute("data-results", "false");
      const resultItems = resultsList.querySelectorAll("li");

      resultItems.forEach((item) => item.remove());
    };

    const handleQueryParam = (query: string) => {
      queryStringHandler.replace({ q: query });
    };

    const handlePageParam = (page: string) => {
      queryStringHandler.replace({ pg: page });
    };

    const handleFetchResults = async (query: string, pg: string) => {
      try {
        const url = `/api/search?q=${query}&pg=${pg}&c=10`;

        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const data = await response.json();

        results = [];
        handleRemoveResults();
        wrapper.setAttribute("data-loading", "false");

        if (data.searchResults?.items && data.searchResults.items.length > 0) {
          results = data.searchResults.items;
        }

        if (data.formattedResultsMessage && data.formattedResultsMessage.includes("{term}")) {
          const formattedMessage = data.formattedResultsMessage.replace(`"{term}"`, "");
          const prefix = document.createTextNode(formattedMessage || "");
          const searchTerm = document.createElement("span");

          formattedTitle.replaceChildren();
          searchTerm.className = "search-formatted-term";
          searchTerm.textContent = `“${query}”`;
          formattedTitle.append(prefix, searchTerm);
        } else {
          formattedTitle.textContent = data.formattedResultsMessage || "";
        }

        handleResultItems();
        initSearchModals();

        if (data.searchResults.totalPages > 1) {
          handleCreatePagination(data.searchResults.totalPages);
          paginationWrapper.setAttribute("data-show", "true");
          totalPagesResult = data.searchResults.totalPages;
          handleDisabledPagination();
        } else {
          paginationWrapper.setAttribute("data-show", "false");
          totalPagesResult = 1;
          handleDisabledPagination();
        }
        wrapper.setAttribute("data-noquery", "false");
      } catch (error) {
        console.error("Error during GET request:", error);
        wrapper.setAttribute("data-loading", "false");
      }
    };

    const handleClearSearch = () => {
      searchInput.value = "";
    };

    const handleSubmitSearch = () => {
      if (searchInput.value.length > 0) {
        handleFetchResults(searchInput.value, "1");
        handleQueryParam(searchInput.value);
        handlePageParam("1");
      }
    };

    const handleKeydownEvent = (e: KeyboardEvent) => {
      if (e.code === "Enter") {
        handleSubmitSearch();
      }
    };

    searchInput.addEventListener("keydown", handleKeydownEvent);
    submitBtn.addEventListener("click", handleSubmitSearch);
    clearBtn.addEventListener("click", handleClearSearch);

    document.addEventListener("DOMContentLoaded", () => {
      if (queryStringHandler.getValue("q")) {
        handleFetchResults(queryStringHandler.getValue("q") || "", queryStringHandler.getValue("pg") || "1");
        searchInput.value = queryStringHandler.getValue("q") || "";
      } else {
        wrapper.setAttribute("data-noquery", "true");
        wrapper.setAttribute("data-noresults", "true");
      }
    });
  }
}

// deploy to dev and test on prod isn't a great process
